import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';

import { authenticate, loadAuthProperty } from '@/libs/store';
import { setSignageApp, setYoyakuApp } from '@/libs/store/app';
import { closeOnlySideBar, closeSideBar, openSideBar, updateActivePath } from '@/libs/store/common';
import { setError } from '@/libs/store/error';
import { setProgress } from '@/libs/store/progress';
import { undoWarn } from '@/libs/store/warn';
import { loadYoyakuStore } from '@/libs/store/yoyaku';
import { request } from '@/libs/v1/api/client';
import Store from '@/store';

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  {
    path: '/yoyaku/:targetName',
    name: 'Yoyaku',
    component: () => import('@/views/Yoyaku.vue'),
    props: true,
  },
  {
    path: '/yoyaku/:targetName/resource',
    name: 'YoyakuResource',
    component: () => import('@/views/YoyakuResource.vue'),
    props: true,
  },
  {
    path: '/yoyaku/:targetName/form',
    name: 'YoyakuForm',
    component: () => import('@/views/YoyakuForm.vue'),
    props: true,
  },
  {
    path: '/yoyaku/:targetName/payment',
    name: 'YoyakuPayment',
    component: () => import('@/views/YoyakuPayment.vue'),
    props: true,
  },
  {
    path: '/yoyaku/:targetName/confirm',
    name: 'YoyakuConfirm',
    component: () => import('@/views/YoyakuConfirm.vue'),
    props: (route) => {
      const targetName = route.params['targetName'];
      const paymentIntentId = route.query['payment_intent'];
      return { targetName, paymentIntentId };
    },
  },
  {
    path: '/yoyaku/:targetName/success',
    name: 'YoyakuSuccess',
    component: () => import('@/views/YoyakuSuccess.vue'),
    props: true,
  },
  {
    path: '/yoyaku/:targetName/:roomId/:assignmentId',
    name: 'YoyakuInfo',
    component: () => import('@/views/YoyakuInfo.vue'),
    props: true,
  },
  {
    path: '/signage/:targetName/reservations',
    name: 'SignageReservations',
    component: () => import('@/views/SignageReservations.vue'),
    props: (route) => {
      const targetName = route.params['targetName'];
      const roomId = route.query['roomId'];
      const roomIds = Array.isArray(roomId) ? roomId : [roomId];
      return { targetName, roomIds };
    },
  },
  {
    path: '/search/:id',
    name: 'Search',
    component: () => import('@/views/Search.vue'),
    props: true,
  },
  {
    path: '/search/:id/result',
    name: 'SearchResult',
    component: () => import('@/views/SearchResult.vue'),
    props: true,
  },
  {
    path: '/dashboard/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    props: true,
  },
  {
    path: '/dashboard/forget/password',
    name: 'ForgetPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    props: true,
  },
  {
    path: '/dashboard/reset/password/:emailAddress',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue'),
    props: true,
  },
  {
    path: '/dashboard/reset/email',
    name: 'ResetEmail',
    component: () => import('@/views/ResetEmail.vue'),
    props: true,
  },
  {
    path: '/dashboard/activate/:hashId/:emailAddress',
    name: 'activation',
    component: () => import('@/views/Activation.vue'),
    props: true,
  },
  {
    path: '/dashboard/home',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/assignment/management',
    name: 'AssignmentManagement',
    component: () => import('@/views/AssignmentManagement.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/analyzation',
    name: 'Analyzation',
    component: () => import('@/views/Analyzation.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/customer/management',
    name: 'CustomerManagement',
    component: () => import('@/views/CustomerManagement.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/camera',
    name: 'Camera',
    component: () => import('@/views/Camera.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/lock/history',
    name: 'LockHistory',
    component: () => import('@/views/LockHistory.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/lock/control',
    name: 'LockControl',
    component: () => import('@/views/LockControl.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/resource/management',
    name: 'ResourceManagement',
    component: () => import('@/views/ResourceManagement.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/space/setting',
    name: 'SpaceSetting',
    component: () => import('@/views/SpaceSetting.vue'),
    meta: { requiresAuth: true, sideBar: true },
    props: true,
  },
  {
    path: '/dashboard/change/email',
    name: 'ChangeEmail',
    component: () => import('@/views/ChangeEmail.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/dashboard/change/activate/:newEmail',
    name: 'changeActivation',
    component: () => import('@/views/ChangeActivation.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/dashboard/change/password',
    name: 'ChangePassword',
    component: () => import('@/views/ChangePassword.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/dashboard/notification',
    name: 'Notification',
    component: () => import('@/views/Notification.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/dashboard/user/control',
    name: 'UserControl',
    component: () => import('@/views/UserControl.vue'),
    meta: { requiresAuth: true, sideBar: 'only' },
    props: true,
  },
  {
    path: '/dashboard/coupon/control',
    name: 'CouponControl',
    component: () => import('@/views/CouponControl.vue'),
    meta: { requiresAuth: true, sideBar: 'only' },
    props: true,
  },
  {
    path: '/dashboard/office/management',
    name: 'OfficeManagement',
    component: () => import('@/views/OfficeManagement.vue'),
    meta: { requiresAuth: true, sideBar: 'only' },
    props: true,
  },
  {
    path: '/dashboard/office/register',
    name: 'OfficeRegister',
    component: () => import('@/views/OfficeRegister.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  { path: '/', redirect: '/dashboard/login' },
  {
    name: 'NotFound',
    path: '*',
    component: () => import('@/views/NotFound.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  try {
    const { path, params, name, matched } = to;
    const login = from.path === '/dashboard/login';

    // authentication
    if (matched.some((record) => record.meta.requiresAuth)) {
      const ok = await authenticate();
      if (!ok) {
        next({ path: '/dashboard/login' });
        return;
      }
      if (login) {
          console.log('success');
      }
      const targetName = Store.getters.targetName;
      if (login || !targetName) {
        setProgress(true);
        await loadAuthProperty();
        setProgress(false);
      }
    }

    updateActivePath(path);

    // Resolve Warn
    undoWarn();

    // Sidebar Check
    if (matched.some((record) => record.meta.sideBar === true)) {
      openSideBar();
    } else if (matched.some((record) => record.meta.sideBar === 'only')) {
      closeOnlySideBar();
    } else if (matched.some((record) => record.meta.requiresAuth && !record.meta.sideBar)) {
      closeSideBar();
    }

    // Yoyaku
    if (name.indexOf('Yoyaku') >= 0) {
      setYoyakuApp();
      const targetName = params.targetName;
      await loadYoyakuStore(targetName);
      const { property } = Store.getters.yoyaku;
      // WEB予約に関しては新規予約画面(yoyaku)だけフラグOFFの場合は404とする
      const isYoyakuRoute = name === 'Yoyaku';
      if (!property || !property.enabled || (isYoyakuRoute && !property.registrationFlag)) {
        next({ path: '/dashboard/notfound' });
        return;
      }
      document.title = property.propertyName;
    }

    // Signage
    if (name.indexOf('Signage') >= 0) {
      setSignageApp();
    }

    // Check roomId
    if (name === 'Search') {
      const roomId = params.id;
      await request(`/v1/api/check/${roomId}`, 'get');
    }

    next();
  } catch (err) {
    console.log('jere', err);
    setError(err.data.msg);
  }
});

export default router;
